import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDate, fmtCurrency } from "../lib/status";
import { CheckCircle2, MessageSquareWarning, Ban, X, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const STATUS_META = {
  new: { label: "New", cls: "bg-orange-100 text-orange-800 border-orange-200" },
  viewed: { label: "Viewed", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  correction_requested: { label: "More info requested", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed_by_business: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  not_found: { label: "Not found", cls: "bg-stone-100 text-stone-700 border-stone-200" },
  not_our_company: { label: "Not our company", cls: "bg-stone-100 text-stone-700 border-stone-200" },
};

const FILTERS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "correction_requested", label: "Awaiting customer" },
  { value: "confirmed_by_business", label: "Confirmed" },
  { value: "not_found", label: "Not found" },
];

export default function VerificationRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/verification-requests").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);

  const quickAction = async (req, action, message) => {
    try {
      await api.post(`/verification-requests/${req.id}/respond`, { action, message });
      toast.success("Response sent to customer");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="verification-requests-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Customer Requests</h1>
        <p className="text-slate-600 mt-1">Customers asking you to verify a booking taken outside ConfirmPH.</p>
      </div>

      <div className="flex flex-wrap gap-1">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`vr-filter-${f.value}`}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              filter === f.value ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        {loading && <div className="p-6 text-slate-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center" data-testid="vr-empty">
            <div className="text-slate-700 font-medium">No customer requests yet</div>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              These appear when customers ask your business to confirm a booking through ConfirmPH.
            </p>
          </div>
        )}
        <ul className="divide-y divide-slate-100">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
            const isOpen = r.status === "new" || r.status === "viewed";
            return (
              <li key={r.id} className="p-4" data-testid={`vr-row-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      {r.customer_name}
                      {r.is_possible_duplicate && (
                        <span className="inline-flex items-center gap-1 text-amber-800 text-xs bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`vr-duplicate-${r.id}`}>
                          <AlertTriangle className="w-3 h-3" /> Possible duplicate
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{r.customer_email}{r.customer_phone ? ` · ${r.customer_phone}` : ""}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}>{meta.label}</span>
                </div>
                <div className="mt-2 grid sm:grid-cols-2 gap-1 text-sm text-slate-700">
                  <div><span className="text-slate-500">Vehicle/service:</span> {r.vehicle_or_service || "-"}</div>
                  <div><span className="text-slate-500">Dates:</span> {r.booking_start ? fmtDate(r.booking_start) : "-"} → {r.booking_end ? fmtDate(r.booking_end) : "-"}</div>
                  <div><span className="text-slate-500">Pickup:</span> {r.pickup_location || "-"}</div>
                  <div><span className="text-slate-500">Price:</span> {r.price != null ? fmtCurrency(r.price) : "-"}</div>
                  {r.advance != null && (
                    <div><span className="text-slate-500">Advance (as stated by the customer):</span> {fmtCurrency(r.advance)}</div>
                  )}
                  {r.deposit != null && (
                    <div><span className="text-slate-500">Security deposit (as stated by the customer):</span> {fmtCurrency(r.deposit)}</div>
                  )}
                  {r.service_mode && (
                    <div><span className="text-slate-500">Service:</span> {{ self_drive: "Self-drive", with_driver: "With driver", unsure: "Customer unsure" }[r.service_mode] || r.service_mode}</div>
                  )}
                </div>
                {r.message && <p className="mt-2 text-sm bg-slate-50 border border-slate-100 rounded-md p-2">{r.message}</p>}
                {r.business_message && (
                  <p className="mt-2 text-sm bg-amber-50 border border-amber-200 rounded-md p-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-amber-800">Your reply:</span> {r.business_message}
                  </p>
                )}
                {r.resulting_booking_id && (
                  <div className="mt-2 text-sm">
                    <Link to={`/app/bookings/${r.resulting_booking_id}`} className="text-[#1B4332] font-medium hover:underline" data-testid={`vr-booking-link-${r.id}`}>
                      View created booking →
                    </Link>
                  </div>
                )}
                <div className="text-xs text-slate-500 mt-2">Received {fmtDate(r.created_at)}</div>

                {isOpen && (
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                    <button
                      onClick={() => setModal({ type: "confirm", request: r })}
                      data-testid={`vr-confirm-${r.id}`}
                      className="px-3 py-1.5 rounded-lg btn-brand text-sm font-medium inline-flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Confirm &amp; create booking
                    </button>
                    <button
                      onClick={() => setModal({ type: "request_correction", request: r })}
                      data-testid={`vr-correction-${r.id}`}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1.5"
                    >
                      <MessageSquareWarning className="w-3.5 h-3.5" /> Ask for more info
                    </button>
                    <button
                      onClick={() => quickAction(r, "mark_not_found")}
                      data-testid={`vr-not-found-${r.id}`}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1.5"
                    >
                      Not found
                    </button>
                    <button
                      onClick={() => quickAction(r, "mark_not_our_company")}
                      data-testid={`vr-not-our-${r.id}`}
                      className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 text-sm inline-flex items-center gap-1.5"
                    >
                      <Ban className="w-3.5 h-3.5" /> Not our company
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {modal && (
        <ResponseModal modal={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function ResponseModal({ modal, onClose, onDone }) {
  const { type, request } = modal;
  const [vehicles, setVehicles] = useState([]);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    item_id: "",
    start_datetime: request.booking_start ? toLocal(request.booking_start) : "",
    end_datetime: request.booking_end ? toLocal(request.booking_end) : "",
    price: request.price ?? 0,
    deposit_required: request.deposit ?? 0,
    pickup_location: request.pickup_location || "",
    customer_visible_notes: "Please bring valid ID and license.",
    send_confirmation_now: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmationLink, setConfirmationLink] = useState(null);
  const { panelRef } = useAccessibleOverlay(true, confirmationLink ? onDone : onClose);

  useEffect(() => {
    if (type === "confirm") {
      api.get("/vehicles").then((r) => {
        const list = r.data.filter((v) => v.status !== "retired");
        setVehicles(list);
        if (list[0]) setForm((f) => ({ ...f, item_id: list[0].id }));
      });
    }
  }, [type]);

  function toLocal(iso) {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let payload;
      if (type === "confirm") {
        payload = {
          action: "confirm",
          item_id: form.item_id,
          start_datetime: new Date(form.start_datetime).toISOString(),
          end_datetime: new Date(form.end_datetime).toISOString(),
          price: Number(form.price) || 0,
          deposit_required: Number(form.deposit_required) || 0,
          pickup_location: form.pickup_location,
          customer_visible_notes: form.customer_visible_notes,
          send_confirmation_now: form.send_confirmation_now,
        };
      } else {
        payload = { action: "request_correction", message };
      }
      const { data: res } = await api.post(`/verification-requests/${request.id}/respond`, payload);
      if (res.confirmation_link) {
        setConfirmationLink(res.confirmation_link);
        toast.success(`Booking ${res.booking_reference} created`);
      } else {
        toast.success("Response sent");
        onDone();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(confirmationLink);
    toast.success("Copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" data-testid="vr-modal">
      <div className="min-h-full flex items-center justify-center p-4">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="verification-response-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-xl w-full p-6 my-4">
        <div className="flex items-center justify-between">
          <h2 id="verification-response-title" className="font-display text-xl font-semibold">
            {type === "confirm" ? "Confirm & create booking" : "Ask for more info"}
          </h2>
          <button type="button" aria-label="Close verification response" onClick={confirmationLink ? onDone : onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="vr-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {confirmationLink ? (
          <div className="mt-4 space-y-3" data-testid="vr-modal-success">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-sm font-medium text-emerald-900">Booking created and confirmation email sent (mock).</div>
              <div className="mt-2 text-xs font-mono break-all text-emerald-900">{confirmationLink}</div>
              <button onClick={copy} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-xs" data-testid="vr-modal-copy">
                <Copy className="w-3 h-3" /> Copy link
              </button>
            </div>
            <button onClick={onDone} className="w-full px-4 py-2.5 rounded-lg btn-brand font-medium" data-testid="vr-modal-done">Done</button>
          </div>
        ) : type === "confirm" ? (
          <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-3" data-testid="vr-confirm-form">
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Vehicle</span>
              <select data-testid="vr-confirm-vehicle" value={form.item_id} onChange={set("item_id")} required className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]">
                <option value="">Select a vehicle…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate_number ? ` (${v.plate_number})` : ""}</option>)}
              </select>
            </label>
            <MF label="Start" type="datetime-local" required testId="vr-confirm-start" value={form.start_datetime} onChange={set("start_datetime")} />
            <MF label="End" type="datetime-local" required testId="vr-confirm-end" value={form.end_datetime} onChange={set("end_datetime")} />
            <MF label="Price (PHP)" type="number" testId="vr-confirm-price" value={form.price} onChange={set("price")} />
            <MF label="Security deposit (PHP)" type="number" testId="vr-confirm-deposit" value={form.deposit_required} onChange={set("deposit_required")} />
            <MF label="Pickup location" testId="vr-confirm-pickup" value={form.pickup_location} onChange={set("pickup_location")} className="sm:col-span-2" />
            <label className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Customer-visible notes</span>
              <textarea data-testid="vr-confirm-notes" value={form.customer_visible_notes} onChange={set("customer_visible_notes")} rows={2} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.send_confirmation_now} onChange={set("send_confirmation_now")} data-testid="vr-confirm-send" />
              <span>Send confirmation link to customer now</span>
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
              <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="vr-confirm-submit">
                {submitting ? "Creating…" : "Create booking"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="vr-correction-form">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Message to {request.customer_name}</span>
              <textarea
                data-testid="vr-correction-message"
                value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required
                placeholder="e.g. Hi, could you share the booking reference or proof of your booking? We can't find a match in our records yet."
                className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F]"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
              <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="vr-correction-submit">
                {submitting ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}

function MF({ label, value, onChange, type = "text", required, testId, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F]" />
    </label>
  );
}
